import { Alert, BottomBar, Box, Gap, Icon, Modal, SaveBar, Textarea } from '@salecycle/ui-component-library';
import * as RestApi from 'core/network/RestApi';
import { RestApiError } from 'core/network/RestApi';
import useNotifications from 'core/notifications/useNotifications';
import React from 'react';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { generatePath } from 'react-router';
import { useHistory, useParams } from 'react-router-dom';
import { OffsiteCampaignEndpoints } from 'sections/offsite-campaigns/constants/api-endpoints';
import { OffsiteCampaignRoute } from 'sections/offsite-campaigns/routes/offsite-campaign-routes';
import { StateBadge } from '../../../components/StateBadge';
import {
  IOffsiteCampaign,
  addDefaultToFirstVariant,
  addVersion2ToCampaign,
  isVariantControlGroup,
  campaignHasMultipleControlGroups,
  sortAndGenerateCyclesNames,
  campaignHasSharedSplitTestIds,
} from '../../../entities/offsiteCampaign';
import { getActivityState } from '../../../utils/activity-state';
import {
  useHasOriginalSplitTestEnded,
  useHasSplitTestEnded,
  useHasSplitTestStarted,
  useOffsiteCampaignEditorDispatch,
  useOffsiteCampaignEditorState,
} from '../../context/UseOffsiteCampaignEditorContext';
import { useFeatureFlags } from 'core/app/AppContext';
import { uuid } from 'utils';
export const Toolbar: FC = () => {
  const { t } = useTranslation('campaigns');
  const dispatch = useOffsiteCampaignEditorDispatch();
  const { showNotification } = useNotifications();
  const { siteId, campaignId } = useParams<{ siteId: string; campaignId: string }>();
  const { push } = useHistory();
  const {
    hasUnsavedCampaignChange,
    hasOrderChange,
    unsavedSegmentChanges,
    hasMissingFields,
    campaign,
    originalCampaign,
  } = useOffsiteCampaignEditorState();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const discardChanges = () => {
    dispatch({
      type: 'DISCARD_CHANGES',
    });
  };

  const { flagEnabled } = useFeatureFlags();
  const splitTestFlag = flagEnabled('ld-split-test-scheduling');
  const controlGroupFlag = flagEnabled('ld-control-group');

  const allFiltersAreSet = () => {
    for (const s of campaign.segments) {
      for (const sf of s.segmentFilters) {
        if (!sf.options.length || sf.value === '') {
          return false;
        }
      }
    }
    return true;
  };

  const allFiltersAreUnique = () => {
    for (const s of campaign.segments) {
      const filters: string[] = [];
      for (const sf of s.segmentFilters) {
        const filterString = [...sf.options, sf.comparison, sf.value].join();
        if (filters.includes(filterString)) {
          return false;
        }
        filters.push(filterString);
      }
    }
    return true;
  };

  const defaultIsControlGroup = () => {
    // If we don't have the feature flags, or it's not a split test, ignore this
    if (!controlGroupFlag || !splitTestFlag || campaign.segments.length < 1) {
      return false;
    }

    // In each segment, check if the first variant (the default) has no cycles
    for (const s of campaign.segments) {
      if (isVariantControlGroup(s.percentageSplits[0])) {
        return true;
      }
    }

    return false;
  };

  // Count the number of control groups per segment
  // If the given segment has multiple control groups, return true and return immediately
  const multipleControlGroups = () => {
    if (!controlGroupFlag) {
      return false;
    }

    return campaignHasMultipleControlGroups(campaign);
  };

  const putCampaignMutation = useMutation((offsiteCampaign: IOffsiteCampaign) =>
    RestApi.put(generatePath(OffsiteCampaignEndpoints.OFFSITE_PUT, { siteId, campaignId }), offsiteCampaign),
  );

  const updateSplitTestId = (originalSegmentId: string, newSplitTestID: string) => {
    dispatch({
      type: 'UPDATE_SPLIT_TEST_ID',
      payload: {
        segmentId: originalSegmentId,
        splitTestId: newSplitTestID,
      },
    });
  };

  // The split test ID should only change if we have EXTENDED an EXPIRED split test
  // Moving a split test end date towards the past will have no effect on reporting
  const checkSplitTestEndDateChangedToCurrentOrFuture = () => {
    const { splitTestEndDate: originalSplitTestEndDate } = originalCampaign;
    const { splitTestEndDate: newTestEndDate } = campaign;

    return (
      originalSplitTestEndDate && // if the campaign had a split test end date at page load
      originalSplitTestEndDate <= newTestEndDate && // and the newer date is more recent or equal
      newTestEndDate >= new Date().getTime() && // and the newer date is later or equal to the current datetime
      splitTestFlag
    );
  };

  // Whenever the split test start date changes for any reason, we will need to change
  // the split test ID
  const checkSplitTestStartDateBeenChanged = () => {
    const { splitTestStartDate: originalSplitTestStartDate } = originalCampaign;
    const { splitTestStartDate: newTestStartDate } = campaign;

    return originalSplitTestStartDate && originalSplitTestStartDate !== newTestStartDate && splitTestFlag;
  };

  const checkIfSplitTestStarted = useHasSplitTestStarted();
  const checkIfSplitTestEnded = useHasSplitTestEnded();
  const checkIfOriginalSplitTestFinished = useHasOriginalSplitTestEnded();

  // This function will assign a new campaign split test start date of the current date/time
  // It will ONLY do this if ALL of the following are true
  // - A variant(s) has been added or removed in a segment(s)
  // - At least one of those segments WAS and STILL IS in a split test
  // - The campaign we are about to save is in a live split test
  const newSplitDateStartTimeIfVariantsAddedOrRemoved = (campaign: IOffsiteCampaign) => {
    const updatedSegments = campaign.segments;
    const originalSegments = originalCampaign?.segments;

    if (checkIfSplitTestStarted && !checkIfSplitTestEnded) {
      // if the CURRENT campaign is live, then we need to assign it a new split test ID if the segment variant count has changed
      originalSegments.forEach(originalSegment => {
        updatedSegments.forEach(updatedSegment => {
          // Iterate over the segments in the original campaign vs current state

          if (originalSegment.id === updatedSegment.id) {
            if (originalSegment.percentageSplits.length <= 1) {
              // Original segment was not in a split test - ignore this segment
              return;
            }

            if (updatedSegment.percentageSplits.length <= 1) {
              // New segment is not in a split test - ignore this segment
              return;
            }

            // We are currently in the same segment between old and new, and it WAS and STILL is in a split test

            // Need to assert we haven't added or removed any variants in this segment
            // Best way to check this is to compare percentageSplit IDs before/after
            const variantsBefore: string[] = [''];
            const variantsAfter: string[] = [''];

            originalSegment.percentageSplits.forEach(variantBefore => {
              variantsBefore.push(variantBefore.id);
            });

            updatedSegment.percentageSplits.forEach(variantAfter => {
              variantsAfter.push(variantAfter.id);
            });

            // We have a list of the variants before/after
            variantsBefore.sort();
            variantsAfter.sort();

            const isSame =
              variantsBefore.length === variantsAfter.length &&
              variantsBefore.every(function(element, index) {
                return element === variantsAfter[index];
              });

            // Make sure they are the same
            if (!isSame) {
              // If not, set the split test start time to NOW
              const newStartTime = new Date().getTime();
              dispatch({
                type: 'SET_CAMPAIGN_SPLIT_TEST_START_TIME',
                payload: { splitTestStartDate: newStartTime },
              });
              campaign.splitTestStartDate = newStartTime;

              // and leave this method, no need to set the split test start time again
              return campaign;
            }
          }
        });
      });
    }
    // however, if the CURRENT campaign was finished or scheduled, then we can keep the split test ID
    return campaign;
  };

  const handleSegmentAndSplitTestId = (campaign: IOffsiteCampaign) => {
    const updatedSegments = campaign.segments;
    const originalSegments = originalCampaign?.segments;

    for (let i = 0; i < updatedSegments.length; i++) {
      switch (true) {
        case typeof originalSegments[i] === 'undefined':
          continue;
        case checkSplitTestStartDateBeenChanged():
        case checkIfOriginalSplitTestFinished && checkSplitTestEndDateChangedToCurrentOrFuture():
          if (updatedSegments[i].percentageSplits.length > 1) {
            const newSplitTestID = uuid();
            updateSplitTestId(updatedSegments[i].id, newSplitTestID);
            updatedSegments[i].splitTestId = newSplitTestID;
          }
          continue;
      }
    }

    return campaign;
  };

  const handleSave = () => {
    // We should NEVER get into this state, but just in case...
    if (campaignHasSharedSplitTestIds(campaign)) {
      showNotification('Save Unsuccessful - please amend your campaign', 'error');
      return;
    }

    const campaignToSave = splitTestFlag
      ? newSplitDateStartTimeIfVariantsAddedOrRemoved(
          handleSegmentAndSplitTestId(
            sortAndGenerateCyclesNames(addDefaultToFirstVariant(addVersion2ToCampaign(campaign))),
          ),
        )
      : campaign;
    putCampaignMutation.mutate(campaignToSave, {
      onSuccess: _data => {
        showNotification('Save Successful');
        setIsModalOpen(false);
        dispatch({
          type: 'SAVE_CHANGES',
        });
        dispatch({
          type: 'SET_ORIGINAL_CAMPAIGN_STATE',
          payload: campaignToSave,
        });
      },
      onError: (_error: RestApiError, _variables, _context) => {
        const errorDetails = (_error as RestApiError).response?.data?.errorMessage ?? '';
        showNotification(errorDetails, 'error');
      },
    });
    setLastEditingComment('');
  };
  const [lastEditingComment, setLastEditingComment] = React.useState('');
  const updateComments = lastEditingComment => {
    setLastEditingComment(lastEditingComment);
    campaign.lastEditingComment = lastEditingComment;
  };

  const onDiscardAndExit = () => {
    dispatch({ type: 'RESET_EDITOR' });
    push(generatePath(OffsiteCampaignRoute.OFFSITE_LIST, { siteId }));
  };

  const CampaignChangeAlert = () => {
    const splitTestExists = originalCampaign.segments.some(s => s.percentageSplits.length > 1);
    if (!splitTestExists) return null;

    const editingLiveCampaign = originalCampaign.state === 'ACTIVE';
    const unpausingCampaign = originalCampaign.state === 'INACTIVE' && campaign.state === 'ACTIVE';
    const editingPausedCampaign = originalCampaign.state === 'INACTIVE' && campaign.state === 'INACTIVE';

    let alertProps: { heading: string; description: string };
    switch (true) {
      case editingLiveCampaign:
        alertProps = {
          heading: t('campaigns:toolBar.alertHeadingSavingCampaign'),
          description: t('campaigns:toolBar.alertDescriptionLiveCampaign'),
        };
        break;
      case unpausingCampaign:
        alertProps = {
          heading: t('campaigns:toolBar.alertHeadingUnpausingCampaign'),
          description: t('campaigns:toolBar.alertDescriptionUnpausingCampaign'),
        };
        break;
      case editingPausedCampaign:
        alertProps = {
          heading: t('campaigns:toolBar.alertHeadingSavingCampaign'),
          description: t('campaigns:toolBar.alertDescriptionPausedCampaign'),
        };
        break;
      default:
        return null;
    }

    return <Alert variant="warning" heading={alertProps.heading} description={alertProps.description} />;
  };

  return (
    <>
      <BottomBar>
        <SaveBar
          onSaveButtonClicked={() => setIsModalOpen(true)}
          onDiscardButtonClicked={discardChanges}
          enableSaveButton={
            (hasUnsavedCampaignChange || unsavedSegmentChanges.length > 0 || hasOrderChange) &&
            !hasMissingFields &&
            allFiltersAreSet() &&
            allFiltersAreUnique() &&
            !defaultIsControlGroup() &&
            !multipleControlGroups()
          }
        />
      </BottomBar>
      <Modal
        heading="Save Campaign?"
        isOpen={isModalOpen}
        variation="clean"
        cancelButtonDisabled={lastEditingComment.length > 0 ? false : true}
        okButtonLabel={t('campaigns:toolBar.discardAndExitButton')}
        okButtonType="secondary"
        onCancelButtonClicked={handleSave}
        cancelButtonLabel={t('campaigns:toolBar.saveAndContinueButton')}
        cancelButtonType="primary"
        onOkButtonClicked={onDiscardAndExit}
        onCloseButtonClicked={() => setIsModalOpen(false)}
      >
        {getActivityState(originalCampaign) !== getActivityState(campaign) && (
          <>
            <Box>
              <StateBadge lifeCycle={originalCampaign} />
              <Box padding="1rem">
                <Icon icon="angle-double-right" />
              </Box>
              <StateBadge lifeCycle={campaign} />
            </Box>
            <Gap bottom={10} />

            <p>
              Your campaign will move from &apos;{getActivityState(originalCampaign)}&apos; to &apos;
              {getActivityState(campaign)}&apos; with the latest changes
            </p>
          </>
        )}
        <div>
          <Textarea
            value={lastEditingComment}
            validationMessage={lastEditingComment.length > 0 ? '' : t('campaigns:toolBar.commentsValidationMessage')}
            validationState={lastEditingComment.length > 0 ? 'complete' : 'warning'}
            ariaLabel={t('campaigns:toolBar.comments')}
            label={t('campaigns:toolBar.comments')}
            onChange={updateComments}
          />
        </div>
        <CampaignChangeAlert />
      </Modal>
    </>
  );
};
